// What to plot for one drum, per machine.
//
// Every field here was checked against what Influx actually records
// (refactor_machine_traking/scripts/_validateChartFields.mjs). That check exists
// because the L120 diameter chart — the most important one on the page — pointed
// at `SET_DIA` and `DIA_ACT_VALUE`, which this line has never recorded: it is a
// TANDEM, with a separate setpoint and gauge per extruder. The chart rendered an
// empty box, and an empty box reads as "the machine did nothing".
//
// `when` gates a group on what the run summary says actually happened. On the
// L120 the two extruders work independently — filler only, jacketing only, or
// both — and showing the idle one's traces is worse than showing nothing: an
// idle extruder holds its last setpoint, so the trace looks like a perfectly
// steady run.

/** chart kinds → components, resolved in TokenMachineCharts.js */
export const CHART_KINDS = { line: 1, double: 1, multi: 1 };

// Fields every machine's summary needs, so the header can say whether the line
// was even running before you read anything else.
/**
 * Which measured field is only meaningful while which gauge was on.
 *
 * A laser reads zero when it is switched off, so without this its idle samples
 * drag the mean of what it measured down with them: on l120 over six hours a
 * filler wall came out 13.65 mm against a 19.13 mm setpoint — a 28% deficit that
 * was entirely the switched-off quarter reading zero.
 */
export const SUMMARY_GATES = {
  l120: {
    FILLING_DIA_ACT_VALUE: 'FILLING_DIA_ON_OFF',
    JACKETING_DIA_ACT_VALUE: 'JACKETING_DIA_ON_OFF'
  },
  // The same trap on the single-extruder line, and it was missed: an ungated
  // `ActualDiameter` summarised a 2.80 mm setpoint as a 2.57 mm wall (-8.4%)
  // with a range starting at 0.00 — the zeros are the laser with nothing in it.
  l75: { ActualDiameter: 'DiameterOnOff' }
};

export const SUMMARY_FIELDS = {
  l120: [
    'E120_F_ON_OFF', 'E120_J_ON_OFF',
    'FILLING_DIA_ON_OFF', 'JACKETING_DIA_ON_OFF',
    'FILLING_SET_DIA', 'FILLING_DIA_ACT_VALUE',
    'JACKETING_SET_DIA', 'JACKETING_DIA_ACT_VALUE',
    'E120_F_ACT_PERCENTEC_VALUE', 'E120_J_ACT_PERCENTEC_VALUE',
    'LINE_SPEED_ACT'
  ],
  l75: [
    'DiameterSetValue', 'ActualDiameter', 'DiameterOnOff', 'LineSpeed',
    // Two extruders: E75 is the main one, E45 lays the stripe. Which of them ran
    // decides which percentage is worth showing.
    'E75_ExtruderOnOff', 'E45_ExtruderOnOff',
    'E75_PercentReferenceValue', 'E45_PercentReferenceValue'
  ],
  dtb1250: ['ACT_RAMP_VALUE_PER', 'BOW_ACT_TWIST', 'BOW_LAY_LENGHT', 'TORQE_ACT_PERCENTEC'],
  skip2250: ['ACT_SPEED', 'BOW_LAY_LENGHT', 'WINDING_ACT_TENSION', 'TTH1_ON_OFF'],
  ath600: ['LINE_SPEED'],
  l70: ['LINE_SPEED'],
  dhc600: ['LINE_SPEED'],
  ach300: ['ACT_LINE_SPEED', 'SET_SPEED']
};

export const tokenModalMachineCharts = {
  // ── L120 — tandem extrusion ────────────────────────────────────────────────
  // Order matters: the diameter of whichever extruder ran comes first, because
  // it is the only trace that says whether the LOT is good. Everything else
  // explains it.
  l120: [
    {
      key: 'filler-diameter',
      kind: 'double',
      fields: 'FILLING_SET_DIA,FILLING_DIA_ACT_VALUE',
      title: 'Filler — set vs measured diameter',
      note: 'The wall, as it was made. Drift here is the lot.',
      // Only when this gauge was actually measuring. Without the gate the trace
      // is the last value an idle laser held, which looks like a flawless run.
      when: s => on(s, 'FILLING_DIA_ON_OFF')
    },
    {
      key: 'jacketing-diameter',
      kind: 'double',
      fields: 'JACKETING_SET_DIA,JACKETING_DIA_ACT_VALUE',
      title: 'Jacketing — set vs measured diameter',
      note: 'The wall, as it was made. Drift here is the lot.',
      when: s => on(s, 'JACKETING_DIA_ON_OFF')
    },
    {
      key: 'filler-screw',
      kind: 'line',
      fields: 'E120_F_SCREW_ACT_RPM',
      title: 'Filler screw (E-120/F) — RPM',
      note: 'Output. A step here with no step in the diameter means the line speed moved with it.',
      when: s => on(s, 'E120_F_ON_OFF')
    },
    {
      key: 'jacketing-screw',
      kind: 'line',
      fields: 'E120_J_SCREW_ACT_RPM',
      title: 'Jacketing screw (E-120/J) — RPM',
      note: 'Output. A step here with no step in the diameter means the line speed moved with it.',
      when: s => on(s, 'E120_J_ON_OFF')
    },
    {
      key: 'filler-percent',
      kind: 'line',
      fields: 'E120_F_ACT_PERCENTEC_VALUE',
      title: 'Filler (E-120/F) — drive %',
      note: 'How hard the drive was working. Read against the screw RPM: the same output at a higher percentage is a screw fighting the compound.',
      when: s => on(s, 'E120_F_ON_OFF')
    },
    {
      key: 'jacketing-percent',
      kind: 'line',
      fields: 'E120_J_ACT_PERCENTEC_VALUE',
      title: 'Jacketing (E-120/J) — drive %',
      note: 'How hard the drive was working. Read against the screw RPM: the same output at a higher percentage is a screw fighting the compound.',
      when: s => on(s, 'E120_J_ON_OFF')
    },
    {
      key: 'filler-current',
      kind: 'line',
      fields: 'E120_F_CURRENT_ACT',
      title: 'Filler extruder (E-120/F) — current',
      note: 'Load. Climbing current at steady RPM is compound that is running cold or a screen packing up.',
      when: s => on(s, 'E120_F_ON_OFF')
    },
    {
      key: 'jacketing-current',
      kind: 'line',
      fields: 'E120_J_CURRENT_ACT',
      title: 'Jacketing extruder (E-120/J) — current',
      note: 'Load. Climbing current at steady RPM is compound that is running cold or a screen packing up.',
      when: s => on(s, 'E120_J_ON_OFF')
    },
    {
      key: 'filler-temps',
      kind: 'multi',
      fields: 'E120_F_ACT_TEMP_Z1,E120_F_ACT_TEMP_Z2,E120_F_ACT_TEMP_Z3,E120_F_ACT_TEMP_Z4,E120_F_ACT_TEMP_Z5,E120_F_ACT_TEMP_HEAD1,E120_F_ACT_TEMP_HEAD2',
      title: 'Filler (E-120/F) — barrel and head temperatures',
      note: 'A zone that wandered during the run is the first thing to check against a diameter that wandered with it.',
      when: s => on(s, 'E120_F_ON_OFF')
    },
    {
      key: 'jacketing-temps',
      kind: 'multi',
      fields: 'E120_J_ACT_TEMP_Z1,E120_J_ACT_TEMP_Z2,E120_J_ACT_TEMP_Z3,E120_J_ACT_TEMP_Z4,E120_J_ACT_TEMP_Z5,E120_J_ACT_TEMP_HEAD1,E120_J_ACT_TEMP_HEAD2',
      title: 'Jacketing (E-120/J) — barrel and head temperatures',
      note: 'A zone that wandered during the run is the first thing to check against a diameter that wandered with it.',
      when: s => on(s, 'E120_J_ON_OFF')
    },
    {
      key: 'line-speed',
      kind: 'line',
      fields: 'LINE_SPEED_ACT',
      title: 'Line speed',
      note: 'Diameter follows speed. Read this beside any excursion above.'
    },
    {
      key: 'caterpillars',
      kind: 'double',
      fields: 'ENTRY_CAT_ACT_PERCENTEC_VALUE,EXIT_CAT_ACT_PERCENTEC_VALUE',
      title: 'Caterpillars — entry vs exit',
      note: 'A gap between the two is the cable being stretched between them.'
    },
    { key: 'meter', kind: 'line', fields: 'METER_ACT', title: 'Metre counter' }
  ],

  // ── L75 — single extruder ──────────────────────────────────────────────────
  l75: [
    {
      key: 'diameter',
      kind: 'double',
      fields: 'DiameterSetValue,ActualDiameter',
      title: 'Set vs measured diameter',
      note: 'The wall, as it was made.'
    },
    {
      key: 'e75-percent',
      kind: 'line',
      fields: 'E75_PercentReferenceValue',
      title: 'E-75 (main) — drive %',
      note: 'How hard the main extruder was working.',
      when: s => on(s, 'E75_ExtruderOnOff')
    },
    {
      key: 'e45-percent',
      kind: 'line',
      fields: 'E45_PercentReferenceValue',
      title: 'E-45 (stripe) — drive %',
      note: 'Only on a striped run. A gap here with the main extruder steady is a stripe that stopped.',
      when: s => on(s, 'E45_ExtruderOnOff')
    },
    {
      key: 'e75-screw',
      kind: 'line',
      fields: 'E75_ScrewRpmValue',
      title: 'E-75 screw — RPM',
      when: s => on(s, 'E75_ExtruderOnOff')
    },
    {
      key: 'e45-screw',
      kind: 'line',
      fields: 'E45_ScrewRpmValue',
      title: 'E-45 screw — RPM',
      when: s => on(s, 'E45_ExtruderOnOff')
    },
    { key: 'line-speed', kind: 'line', fields: 'LineSpeed', title: 'Line speed' },
    { key: 'meter', kind: 'line', fields: 'METER_ACT', title: 'Metre counter' }
  ],

  // ── DTB1250 — bunching / stranding ─────────────────────────────────────────
  // Nothing measures the product here: a strand's quality IS its lay length and
  // the tension it was pulled at, so those are the traces that matter.
  dtb1250: [
    {
      key: 'twist',
      kind: 'double',
      fields: 'BOW_MAX_RPM,BOW_ACT_TWIST',
      title: 'Bow — max vs actual twist (rot/min)',
      note: 'Twist against line speed is the lay. A dip here without a dip in speed is a longer lay than the recipe.'
    },
    {
      key: 'lay-length',
      kind: 'line',
      fields: 'BOW_LAY_LENGHT',
      title: 'Lay length (mm)',
      note: 'The setpoint. A step means the recipe changed mid-drum.'
    },
    {
      key: 'tension',
      kind: 'line',
      fields: 'TORQE_ACT_PERCENTEC',
      title: 'Capstan pull — torque (%)',
      note: 'Tension. Spikes are a pay-off dragging or a wire catching.'
    },
    {
      key: 'speed',
      kind: 'double',
      fields: 'MAX_RAMP_VALUE_PER,ACT_RAMP_VALUE_PER',
      title: 'Line speed — max vs actual',
      note: 'Every stop in the drum, and how long each took to come back up.'
    },
    {
      key: 'currents',
      kind: 'double',
      fields: 'BOW_CURRENT,CAPSTAN_CURRENT',
      title: 'Bow vs capstan current',
      note: 'Load. Rising bow current at steady twist is friction the line did not have at the start.'
    },
    {
      key: 'traverse',
      kind: 'multi',
      fields: 'TRAVERS_LEFT_PULS_LIMIT,TRAVERS_ACT_PULS,TRAVERS_RIGHT_PULS_LIMIT',
      title: 'Traverse — the winding limits and where it actually was',
      // Pulses, not degrees: the multi-series chart is named after its first use
      // and was captioning this "DTB1250 Temp Zones" on a °C axis.
      unit: 'pulses',
      note: 'How the drum was laid. A traverse that stopped sweeping is a drum wound in one place.'
    },
    {
      key: 'traverse-pitch',
      kind: 'line',
      fields: 'TRAVERSE_CABLES_DIA',
      title: 'Traverse pitch (mm)',
      note: 'Spacing per turn — it should match the strand being wound.'
    },
    { key: 'meter', kind: 'line', fields: 'METER_ACT', title: 'Metre counter' }
  ],

  // ── SKIP2250 — skip stranding with a taping head ───────────────────────────
  skip2250: [
    {
      key: 'lay-length',
      kind: 'line',
      fields: 'BOW_LAY_LENGHT',
      title: 'Lay length (mm)',
      note: 'The setpoint. A step means the recipe changed mid-drum.'
    },
    {
      key: 'speed',
      kind: 'double',
      fields: 'MAX_RAMP_LIMIT_VALUE,ACT_RAMP_VALUE',
      title: 'Line speed — limit vs actual',
      note: 'Every stop in the drum.'
    },
    {
      key: 'winding-tension',
      kind: 'double',
      fields: 'WINDING_SET_TENSION,WINDING_ACT_TENSION',
      title: 'Take-up — set vs actual tension',
      note: 'A drum wound loose comes apart in the rack; wound tight it crushes what is under it.'
    },
    {
      key: 'currents',
      kind: 'double',
      fields: 'BOW_CURRENT,CAPSTAN_CURRENT',
      title: 'Bow vs capstan current',
      note: 'Load.'
    },
    {
      key: 'tape-diameter',
      kind: 'double',
      fields: 'TTH1_A_ZONE_LAZER_ACT_DIA,TTH1_B_ZONE_LAZER_ACT_DIA',
      title: 'Taping head — measured diameter over tape A and B',
      note: 'The only measurement of the product on this line.',
      when: s => on(s, 'TTH1_ON_OFF')
    },
    {
      key: 'tape-lay',
      kind: 'double',
      fields: 'TTH1_LAY_LENTH,TTH1_RPM',
      title: 'Taping head — lay length and RPM',
      note: 'Overlap. RPM against line speed is how much tape went on per metre.',
      when: s => on(s, 'TTH1_ON_OFF')
    },
    {
      key: 'traverse',
      kind: 'double',
      fields: 'TARVERSE_RPM,TRAVERSE_CURRENT',
      title: 'Traverse — RPM and current',
      note: 'How the drum was laid.'
    },
    { key: 'meter', kind: 'line', fields: 'METER_ACT', title: 'Metre counter' }
  ],

  // ── the simpler lines ──────────────────────────────────────────────────────
  ath600: [
    { key: 'line-speed', kind: 'line', fields: 'LINE_SPEED', title: 'Line speed' },
    { key: 'meter', kind: 'line', fields: 'METER_ACT', title: 'Metre counter' }
  ],

  l70: [
    { key: 'line-speed', kind: 'line', fields: 'LINE_SPEED', title: 'Line speed' },
    { key: 'meter', kind: 'line', fields: 'METER_ACT', title: 'Metre counter' }
  ],

  dhc600: [
    { key: 'line-speed', kind: 'line', fields: 'LINE_SPEED', title: 'Line speed' }
    // `total_produced` was configured here and on ach300 and has never been
    // recorded on either — it is a machineReport field, not an Influx one.
  ],

  ach300: [
    {
      key: 'line-speed',
      kind: 'double',
      fields: 'SET_SPEED,ACT_LINE_SPEED',
      title: 'Set vs actual line speed'
    }
  ]
};

/** Was this state on for any meaningful part of the window? */
function on(summary, field) {
  const s = summary?.[field];
  if (!s) return false;
  // A gauge switched on for a handful of samples at the very end is a setup
  // touch, not a run. Anything above a twentieth of the drum counts.
  return s.kind === 'state' ? s.onPct >= 5 : false;
}

/**
 * The charts to draw for this machine and this run.
 *
 * With no summary yet (still loading, or the endpoint is unreachable) the gated
 * groups are HELD BACK rather than shown: a diameter trace from an idle laser is
 * a confident-looking lie, and no chart is better than a wrong one.
 */
export function chartsFor(machineId, summary) {
  const defs = tokenModalMachineCharts[machineId] || [];
  return defs.filter(d => (typeof d.when === 'function' ? d.when(summary || {}) : true));
}

export { on as stateWasOn };
